import type { CashFlowCategory } from '@progear/finance-core';
import { PRIORITY_CLS, PRIORITY_LABEL, type CashFlowEntry } from '@/lib/cashFlow';
import { DOC_TYPE_LABEL } from '@/lib/cashFlowFilters';
import { LINE_META } from '@/components/cash-flow/categoryMeta';

/**
 * Pure presentation models for the register (`Ledger.tsx`). Each helper takes the
 * narrowest input it needs and returns a display-only model — no money maths, no JSX
 * (L9: components/helpers never compute money; amounts arrive pre-resolved from
 * finance-core and are only ever *formatted* via `formatEur`). Keeping these pure +
 * unit-tested is what lets the dense register table hit 100% coverage without faking
 * branches in the giant component.
 */

/** Short 3-letter document-type code shown in the merged "Dok" column. The full
 *  Estonian label (DOC_TYPE_LABEL) is the hover title — codes never reinvent the
 *  terminology, they abbreviate it. */
export const DOC_TYPE_CODE: Record<NonNullable<CashFlowEntry['docType']>, string> = {
  invoice: 'ARV',
  prepayment: 'ETT',
  credit_note: 'KRE',
  offer: 'PAK',
  delivery_invoice: 'SAA',
  none: 'MUU',
};

export interface DocCell {
  /** Abbreviated code badge, or null on statutory/loan rows with no docType. */
  code: string | null;
  /** Full Estonian docType label for the hover title, or null. */
  label: string | null;
}

/** Merge the document type into the "Dok" column: a short code badge + a hover label. */
export function docCell(docType: CashFlowEntry['docType']): DocCell {
  if (!docType) return { code: null, label: null };
  return { code: DOC_TYPE_CODE[docType], label: DOC_TYPE_LABEL[docType] };
}

/** A distinct accent class per document type so the "Dok" code is scannable at a
 *  glance (ARV blue · ETT amber · KRE red · PAK purple · SAA green). Token colours
 *  only — no hex — reusing the inherited palette (L8); mirrors the 5-colour chart
 *  ramp. One source so the register badge never hand-picks a tint. */
export const DOC_TYPE_BADGE_CLS: Record<NonNullable<CashFlowEntry['docType']>, string> = {
  invoice: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]',
  prepayment: 'bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]',
  credit_note: 'bg-[hsl(var(--cost))]/15 text-[hsl(var(--cost))]',
  offer: 'bg-[hsl(var(--vatmode))]/15 text-[hsl(var(--vatmode))]',
  delivery_invoice: 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]',
  none: 'bg-muted/40 text-muted-foreground',
};

/** Accent classes for the "Dok" code badge, by document type. `null` (statutory /
 *  loan rows with no docType, which render no badge) → empty string. */
export function docTypeBadgeCls(docType: CashFlowEntry['docType']): string {
  if (!docType) return '';
  return DOC_TYPE_BADGE_CLS[docType];
}

/** 2–3 letter abbreviation per cash-flow line (the "Liik" column). The full label
 *  (LINE_META) stays reachable as the hover title. */
export const LIIK_ABBREV: Record<CashFlowCategory, string> = {
  sale: 'Müü',
  direct_cost: 'Ots',
  operating_cost: 'Teg',
  payroll: 'Töö',
  financial_cost: 'Fin',
  tax_agreement: 'Mak',
  loan: 'Lae',
};

export interface LiikCell {
  abbrev: string;
  label: string;
}

/** Compact "Liik" cell: a short abbreviation + the full label for the hover title. */
export function liikCell(category: CashFlowCategory): LiikCell {
  return { abbrev: LIIK_ABBREV[category], label: LINE_META[category].label };
}

/** Shorten a long project name for the narrow "Projekt" column (full name → hover
 *  title). `null` (no project) stays null. */
export function projectCompact(project: string | null, max = 12): string | null {
  if (project === null) return null;
  return project.length > max ? `${project.slice(0, max - 1)}…` : project;
}

export interface PriorityCell {
  label: string;
  cls: string;
}

/** The priority flag model (KRIITILINE/OLULINE/FOOKUSES) shared by the "Dok" flag,
 *  the register editor and the calendar chip — one source for label + classes
 *  (L14 lens parity). `null` priority → no flag. */
export function priorityCell(priority: CashFlowEntry['priority']): PriorityCell | null {
  if (!priority) return null;
  return { label: PRIORITY_LABEL[priority], cls: PRIORITY_CLS[priority] };
}

/**
 * Decide whether the register's "Halda" popover should open UPWARD to avoid being
 * clipped by the scrolling table body. Pure geometry (no DOM, viewport pixels) so
 * it is unit-tested directly and the giant component stays coverable: open DOWN
 * when the preferred height fits below the trigger inside the scroll container;
 * otherwise open UP only when there is more room above than below.
 */
export function popoverOpensUp(input: {
  triggerTop: number;
  triggerBottom: number;
  containerTop: number;
  containerBottom: number;
  preferredHeight: number;
}): boolean {
  const below = input.containerBottom - input.triggerBottom;
  if (below >= input.preferredHeight) return false;
  const above = input.triggerTop - input.containerTop;
  return above > below;
}
